import {
    Box, Button, Card, CardContent, CardHeader, Chip, Divider,
    FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { getAllAgents } from '../../api/userApi';
import { downloadAgentCp58, downloadBulkCp58 } from '../../api/cp58Api';

// CP58 is for commission earners. Include any user who carries a comm-earning
// role even if they ALSO carry an admin role (e.g. Tan Jin Sheng is Super
// Admin + Leader + Sales Agent — he still earns TOC and needs a CP58).
// Referral mirrors In-house Agent — same commission mechanics, eligible for CP58.
const ELIGIBLE_ROLES = new Set(['Sales Agent', 'In-house Agent', 'Leader', 'Referral']);

export default function Cp58List() {
    const [agents, setAgents] = useState([]);
    const [filter, setFilter] = useState('');
    const [year, setYear] = useState(new Date().getFullYear() - 1);
    const [nonZeroOnly, setNonZeroOnly] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        getAllAgents()
            .then(res => setAgents(res.data || []))
            .catch(err => setError((err.message || 'Unknown error')));
    }, []);

    // Active users with at least one comm-earning role. Multi-role users
    // qualify when ANY role is eligible (so Super Admin + Leader users
    // still appear).
    const eligible = useMemo(() => {
        const list = agents.filter(a => {
            if (a.status !== 'Active') return false;
            const roles = (a.userRoles || []).map(r => r.roleName);
            return roles.some(rn => ELIGIBLE_ROLES.has(rn));
        });
        if (!filter) return list;
        const q = filter.toLowerCase();
        return list.filter(a =>
            (a.username || '').toLowerCase().includes(q)
         || (a.firstName || '').toLowerCase().includes(q)
         || (a.lastName || '').toLowerCase().includes(q)
         || (a.alias || '').toLowerCase().includes(q)
        );
    }, [agents, filter]);

    const handleDownloadOne = async (userId) => {
        setDownloadingId(userId);
        setError('');
        try {
            await downloadAgentCp58(userId, year);
        } catch (err) {
            setError((err.message || 'Unknown error'));
        } finally {
            setDownloadingId(null);
        }
    };

    const handleBulk = async () => {
        setBulkBusy(true);
        setError('');
        try {
            await downloadBulkCp58(year, nonZeroOnly);
        } catch (err) {
            setError((err.message || 'Unknown error'));
        } finally {
            setBulkBusy(false);
        }
    };

    const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Card>
                <CardHeader
                    title="CP58 — Yearly Tax PDFs"
                    subheader="LHDN CP58 generator. Sums approved commission, extra, and cash incentive payouts in the chosen calendar year."
                    titleTypographyProps={{ variant: 'h6' }}
                />
                <Divider />
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Tax Year</InputLabel>
                            <Select
                                value={year}
                                onChange={e => setYear(Number(e.target.value))}
                                label="Tax Year"
                            >
                                {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={<Switch checked={nonZeroOnly} onChange={e => setNonZeroOnly(e.target.checked)} />}
                            label="Non-zero only (bulk export)"
                        />
                        <Button
                            variant="contained"
                            disabled={bulkBusy}
                            onClick={handleBulk}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {bulkBusy ? 'Building zip...' : 'Export All (zip)'}
                        </Button>
                        <TextField
                            size="small"
                            label="Filter agents"
                            value={filter}
                            onChange={e => setFilter(e.target.value)}
                            sx={{ minWidth: 220 }}
                        />
                    </Stack>

                    {error && (
                        <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: 1, fontSize: '0.85rem' }}>
                            {String(error)}
                        </Box>
                    )}

                    <Table size="small" sx={{
                        '& th': {
                            backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0',
                            textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600,
                            letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif",
                            padding: '10px 12px',
                        },
                        '& td': {
                            fontSize: '0.85rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9',
                            fontFamily: "'Poppins', sans-serif", padding: '10px 12px',
                        },
                        '& tbody tr:hover': { backgroundColor: '#F8FAFC' },
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Username</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Group</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {eligible.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography sx={{ fontSize: '0.85rem', color: '#94A3B8', py: 2, textAlign: 'center' }}>
                                            No eligible users.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {eligible.map(a => {
                                // Show "{Last} {First}" so the full name reads naturally
                                // even when an agent has only one of the two filled.
                                const fullName = [a.lastName, a.firstName].filter(Boolean).join(' ') || a.alias || a.username;
                                const primaryRole = (a.userRoles || [])
                                    .map(r => r.roleName)
                                    .find(rn => ELIGIBLE_ROLES.has(rn))
                                    || (a.userRoles?.[0]?.roleName ?? '-');
                                return (
                                    <TableRow key={a.userId} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>{fullName}</TableCell>
                                        <TableCell sx={{ color: '#64748B' }}>{a.username}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={primaryRole}
                                                size="small"
                                                sx={{
                                                    height: 22, fontSize: '0.7rem', fontWeight: 600,
                                                    fontFamily: "'Poppins', sans-serif",
                                                    backgroundColor: '#E0E7FF', color: '#3730A3',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748B' }}>{a.groupName || '-'}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={downloadingId === a.userId}
                                                onClick={() => handleDownloadOne(a.userId)}
                                                sx={{
                                                    textTransform: 'none', borderRadius: '8px',
                                                    fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {downloadingId === a.userId ? 'Generating...' : 'Download'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
}
